import { useEffect, useRef, useState } from 'react';
import { paymentApi } from '../../../api/payment.api.js';

const TERMINAL_STATUSES = ['PAID', 'EXPIRED', 'CANCELLED', 'FAILED'];
// Đang nhận luồng SSE: hỏi thưa (chỉ để dự phòng, và để backend hỏi thẳng API SePay khi webhook không tới).
// Không mở được luồng: hỏi 3 giây/lần như trước.
const POLL_WITH_STREAM_MS = 8000;
const POLL_WITHOUT_STREAM_MS = 3000;
// Hết giờ QR vẫn chờ thêm (khớp EXPIRE_GRACE_MS ở backend): khách chuyển khoản sát giờ, tiền về muộn vẫn được kích hoạt
const EXPIRE_GRACE_MS = 10 * 60 * 1000;
const STREAM_RETRY_MS = 5000;

/**
 * Theo dõi trạng thái 1 đơn thanh toán tới khi đạt trạng thái cuối (PAID/EXPIRED/CANCELLED/FAILED).
 * - Thời gian thực: nghe luồng SSE, webhook SePay báo có tiền là nhận PAID ngay.
 * - Dự phòng: hỏi GET /payments/orders/:id định kỳ (mất kết nối luồng, máy chủ chạy nhiều tiến trình,
 *   webhook không tới thì backend tự hỏi API SePay).
 */
export function useOrderPolling(order) {
  const [status, setStatus] = useState(order?.status || 'PENDING');
  const [paidAt, setPaidAt] = useState(order?.paidAt || null);
  const streamingRef = useRef(false);

  useEffect(() => {
    if (!order?.id) return undefined;
    setStatus(order.status);
    setPaidAt(order.paidAt || null);
    if (TERMINAL_STATUSES.includes(order.status)) return undefined;

    let stopped = false;
    let pollTimer = null;
    let retryTimer = null;
    const controller = new AbortController();
    const stopAt = order.expiredAt ? new Date(order.expiredAt).getTime() + EXPIRE_GRACE_MS : Infinity;

    const finish = (latest) => {
      if (!TERMINAL_STATUSES.includes(latest)) return;
      stopped = true;
      controller.abort();
      clearTimeout(pollTimer);
      clearTimeout(retryTimer);
    };

    const openStream = () => {
      if (stopped) return;
      paymentApi
        .streamOrderStatus(
          order.id,
          (latest) => {
            streamingRef.current = true;
            setStatus(latest);
            finish(latest);
          },
          controller.signal
        )
        .catch(() => {})
        .finally(() => {
          streamingRef.current = false;
          // Luồng bị đóng (proxy cắt, máy chủ khởi động lại): mở lại sau ít giây
          if (!stopped) retryTimer = setTimeout(openStream, STREAM_RETRY_MS);
        });
    };

    const poll = async () => {
      if (stopped) return;
      if (Date.now() > stopAt) {
        setStatus('EXPIRED');
        finish('EXPIRED');
        return;
      }
      try {
        const res = await paymentApi.getOrderStatus(order.id);
        setStatus(res.data.status);
        setPaidAt(res.data.paidAt || null);
        finish(res.data.status);
      } catch (err) {
        // Lỗi mạng tạm thời: thử lại ở lượt kế tiếp
        console.warn('Lỗi kiểm tra trạng thái đơn hàng:', err.message);
      }
      if (!stopped) pollTimer = setTimeout(poll, streamingRef.current ? POLL_WITH_STREAM_MS : POLL_WITHOUT_STREAM_MS);
    };

    openStream();
    pollTimer = setTimeout(poll, POLL_WITHOUT_STREAM_MS);

    return () => {
      stopped = true;
      controller.abort();
      clearTimeout(pollTimer);
      clearTimeout(retryTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id]);

  return { status, paidAt };
}
